/**
 * Crop Cache Service
 * Global service for caching and managing crop data
 * Provides efficient lookup methods and color mapping
 */

import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import {  tap, shareReplay, catchError } from 'rxjs';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { Crop } from '@6grain/masofa-monolith-api-client/dictionaries';
import { ServerTranslateService } from '@services/server-translate.service';
import { getCropColorByName } from '@app/utils/crop-utils';

export interface CachedCrop {
  id: string;
  nameRu?: string;
  nameEn?: string;
  nameUz?: string;
  names?: { [key: string]: string };
  color?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CropCacheService {
  private cropService = inject(CropService);
  private serverTranslate = inject(ServerTranslateService);

  // Observable cache
  private cropsCache$?: Observable<Crop[]>;
  cachedCropsData: Crop[] = [];

  // In-memory maps for fast lookup
  private cropByIdMap = new Map<string, Crop>();
  private cropNameByIdMap = new Map<string, string>();
  private cropColorByIdMap = new Map<string, string>();

  // Loading state
  private isLoading$ = new BehaviorSubject<boolean>(false);
  private isLoaded$ = new BehaviorSubject<boolean>(false);

  constructor() {}

  /**
   * Get all crops from cache or fetch from API
   * Returns cached data immediately if available, prevents unnecessary API calls
   */
  getCrops(forceRefresh: boolean = false): Observable<Crop[]> {
    // If we have cached data and not forcing refresh, return it immediately
    if (this.cachedCropsData.length > 0 && !forceRefresh) {
      return of(this.cachedCropsData);
    }

    // If force refresh, clear the cache
    if (forceRefresh) {
      this.cachedCropsData = [];
      this.cropsCache$ = undefined;
    }

    // Fetch from API only if cache is empty
    this.isLoading$.next(true);

    this.cropsCache$ = this.cropService.dictionariesCropGetByQueryPost({
      take: 1000,
      offset: 0
    }).pipe(
      tap(crops => {
        // Store crops in cache
        this.cachedCropsData = crops;
        this.buildCacheMaps(crops);
        this.isLoading$.next(false);
        this.isLoaded$.next(true);
      }),
      catchError(error => {
        console.error('Error loading crops:', error);
        this.isLoading$.next(false);
        return of([]);
      }),
      shareReplay(1)
    );

    return this.cropsCache$;
  }


  /**
   * Get crop name by ID (translated)
   */
  getCropNameById(cropId: string, translate = false): any {
    if (this.cropNameByIdMap.has(cropId) && !translate) {
      return this.cropNameByIdMap.get(cropId);
    }

    const crop = this.cropByIdMap.get(cropId);
    if (crop) {

      if (translate) {
        return this.serverTranslate.translate(crop, 'names');
      }
      // @ts-ignore
      return crop['names']['en-US'];
    }

    return undefined;
  }

  /**
   * Get crop color by ID
   */
  getCropColorById(cropId: string, defaultColor: string = '#4CAF50'): string {
    if (this.cropColorByIdMap.has(cropId)) {
      return this.cropColorByIdMap.get(cropId)!;
    }
    const cropName = this.getCropNameById(cropId);
    if (cropName) {
      const color = getCropColorByName(cropName, defaultColor);
      this.cropColorByIdMap.set(cropId, color);
      return color;
    }

    return defaultColor;
  }



  /**
   * Check if crops are currently loading
   */
  isLoading(): Observable<boolean> {
    return this.isLoading$.asObservable();
  }

  /**
   * Check if crops have been loaded
   */
  isLoaded(): Observable<boolean> {
    return this.isLoaded$.asObservable();
  }

  /**
   * Clear all caches and force reload on next request
   */
  clearCache(): void {
    this.cropsCache$ = undefined;
    this.cachedCropsData = [];
    this.cropByIdMap.clear();
    this.cropNameByIdMap.clear();
    this.cropColorByIdMap.clear();
    this.isLoaded$.next(false);
  }

  /**
   * Preload crops into cache
   * Useful for eager loading on app initialization
   */
  preloadCrops(): void {
    this.getCrops().subscribe();
  }

  /**
   * Build internal cache maps for fast lookup
   */
  private buildCacheMaps(crops: Crop[]): void {
    this.cropByIdMap.clear();
    this.cropNameByIdMap.clear();
    this.cropColorByIdMap.clear();

    crops.forEach(crop => {
      if (crop.id) {
        // Store crop object
        this.cropByIdMap.set(crop.id, crop);

        // Store translated name with first letter uppercase
        // @ts-ignore
        let name = crop['names']['en-US'];
        if (name) {
          // Capitalize first letter
          name = name.charAt(0).toUpperCase() + name.slice(1);

          this.cropNameByIdMap.set(crop.id, name);
          // Store color
          const color = getCropColorByName(name);
          this.cropColorByIdMap.set(crop.id, color);
        }
      }
    });
  }
}
